import './DagStatus.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Dfns from 'date-fns/fp';
import { Badge } from '@Components/Badge';
import { Button } from '@Components/Button';
import { Design, Placement, Size } from '@Components/designConstants';
import { Page } from '@Components/Page';
import { BPMTable, Header } from '@Components/StickyTable';
import { TableLoading } from '@Components/TableEmptyState';
import { TablePage } from '@Components/TablePage';
import { useAppSelector } from '@src/redux';
import { useSetError } from '@src/redux/modals';
import { isAdminSelector, isInternalSelector, isNOCSelector } from '@src/redux/user';
import { validDate } from '@src/utils/date-utils';
import {
  DATE_TIME_FORMAT,
  RestartMap,
  STATUS_BADGE_DESIGN,
  UPDATE_RESTART_STATUSES_MS
} from './dag-status-constants';
import {
  getDagKey,
  isBatchJobStatus,
  isTerminalStatus,
  isValidDateString,
  omitRestart,
  pruneRestarts
} from './dag-status-utils';
import { DagBuildStatus, restartDagBuild, useDagStatus } from './use-dag-status';

interface StatusBadgeProps {
  status: string;
}

const StatusBadge = ({ status }: StatusBadgeProps) => (
  <Badge
    size={Size.SM}
    design={isBatchJobStatus(status) ? STATUS_BADGE_DESIGN[status] : Design.NEUTRAL}
  >
    {status}
  </Badge>
);

const BASE_HEADERS: Header[] = [
  {
    label: 'Build',
    name: 'batch_job_name',
    flex: 3,
    minFlexWidth: 300,
    renderer: (data: DagBuildStatus) => (
      <a href={data.batch_log_url} target='_blank' rel='noopener noreferrer'>
        {data.batch_job_name}
      </a>
    )
  },
  {
    label: 'Status',
    name: 'status_batch',
    width: 120,
    renderer: (data: DagBuildStatus) => <StatusBadge status={data.status_batch} />
  },
  {
    label: 'Previous Builds',
    name: 'previous_builds',
    width: 300,
    renderer: (data: DagBuildStatus) =>
      !data.previous_builds || data.previous_builds.length === 0 ? (
        'N/A'
      ) : (
        <div className='previous-builds' data-testid={`previous-builds-${getDagKey(data)}`}>
          {data.previous_builds.map(build => (
            <OverlayTrigger
              key={build.batch_log_url}
              placement={Placement.LEFT}
              overlay={
                <Tooltip id={`previous-build-${build.batch_log_url}`}>
                  Status: {build.status_batch}
                  <br />
                  Job End Time: {build.job_end_time}
                </Tooltip>
              }
            >
              <a
                target='_blank'
                rel='noopener noreferrer'
                href={build.batch_log_url}
                className='previous-build'
              >
                <StatusBadge status={build.status_batch} />
              </a>
            </OverlayTrigger>
          ))}
        </div>
      )
  },
  {
    label: 'Expected Completion (Local)',
    name: 'lastmodified',
    width: 260,
    renderer: (data: DagBuildStatus) => {
      if (
        isTerminalStatus(data.status_batch) ||
        !isValidDateString(data.now) ||
        !data.total_expected_runtime
      ) {
        return '-';
      }
      return Dfns.format(
        DATE_TIME_FORMAT,
        Dfns.addSeconds(data.total_expected_runtime, new Date(data.now))
      );
    }
  },
  {
    label: 'Job Start Time',
    name: 'job_start_time',
    width: 200,
    flex: 1,
    renderer: (data: DagBuildStatus) => {
      if (
        !data.job_start_time ||
        !validDate(new Date(data.job_start_time)) ||
        !isValidDateString(data.now)
      ) {
        return '-';
      }
      return Dfns.format(DATE_TIME_FORMAT, new Date(data.job_start_time));
    }
  },
  {
    label: 'Job End Time',
    name: 'job_end_time',
    width: 200,
    flex: 1,
    renderer: (data: DagBuildStatus) => {
      if (
        !data.job_end_time ||
        !validDate(new Date(data.job_end_time)) ||
        !isValidDateString(data.now)
      ) {
        return '-';
      }
      return Dfns.format(DATE_TIME_FORMAT, new Date(data.job_end_time));
    }
  }
];

interface DagStatusTableProps {
  dagStatusMap?: DagBuildStatus[];
  isLoading: boolean;
  error: unknown;
  restarts: RestartMap;
  canRestart: boolean;
  onRestart: (data: DagBuildStatus) => void;
}

const DagStatusTable = ({
  dagStatusMap,
  isLoading,
  error,
  restarts,
  canRestart,
  onRestart
}: DagStatusTableProps) => {
  const headers = useMemo((): Header[] => {
    if (!canRestart) return BASE_HEADERS;
    return [
      ...BASE_HEADERS,
      {
        label: 'Restartable',
        name: 'running',
        width: 140,
        renderer: (data: DagBuildStatus) => {
          if (!isTerminalStatus(data.status_batch)) return null;
          const dagKey = getDagKey(data);
          return (
            <Button
              data-testid={`restart-button-${dagKey}`}
              size={Size.SM}
              loading={dagKey in restarts}
              onClick={() => onRestart(data)}
            >
              Restart
            </Button>
          );
        }
      }
    ];
  }, [canRestart, restarts, onRestart]);

  const renderTableLoading = useCallback(
    () => <TableLoading isLoading={isLoading} error={error} emptyMessage='No builds to show' />,
    [isLoading, error]
  );

  return (
    <TablePage>
      <BPMTable
        // Remount once data loads so the default filter applies.
        key={dagStatusMap ? 'loaded' : 'loading'}
        data={dagStatusMap ?? []}
        headers={headers}
        defaultTokens={{ advanced: ['Build', 'is like', 'MOBIUS'] }}
        defaultAdvancedFilter
        noRowsRenderer={renderTableLoading}
      />
    </TablePage>
  );
};

export const DagStatus = () => {
  const isAdmin = useAppSelector(isAdminSelector);
  const isInternal = useAppSelector(isInternalSelector);
  const isNOC = useAppSelector(isNOCSelector);
  const setError = useSetError();

  const [restarts, setRestarts] = useState<RestartMap>({});
  const hasPendingRestarts = Object.keys(restarts).length > 0;

  const {
    data: dagStatusMap,
    isLoading,
    error,
    refetch
  } = useDagStatus(isInternal || isNOC, hasPendingRestarts ? UPDATE_RESTART_STATUSES_MS : false);

  useEffect(() => {
    if (error) {
      setError({
        message: `Failed to load dag status info: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  useEffect(() => {
    if (!dagStatusMap) return;
    setRestarts(current => pruneRestarts(current, dagStatusMap));
  }, [dagStatusMap]);

  const restartBuild = useCallback(
    async (data: DagBuildStatus) => {
      const { batch_job_name, dag, job_start_time } = data;
      const dagKey = getDagKey(data);
      setRestarts(current => ({ ...current, [dagKey]: { job_start_time } }));
      // A rejected restart never changes the build, so pruneRestarts can't clear it.
      const clearRestart = () => setRestarts(current => omitRestart(current, dagKey));
      try {
        const result = await restartDagBuild(dag, batch_job_name);
        if (!result.success) {
          setError({ message: result.message });
          clearRestart();
        }
      } catch (reportError) {
        setError({
          message: `Failed to restart build. Error: ${reportError.message}`,
          reportError
        });
        clearRestart();
      }
    },
    [setError]
  );

  const handleRestart = useCallback(
    (data: DagBuildStatus) => {
      restartBuild(data);
      refetch();
    },
    [restartBuild, refetch]
  );

  return (
    <Page
      title='Dag Status'
      pageType='Dag Status'
      className='dag-status-page'
      minHeight='600px'
      isLoading={isLoading}
    >
      <DagStatusTable
        dagStatusMap={dagStatusMap}
        isLoading={isLoading}
        error={error}
        restarts={restarts}
        canRestart={isAdmin || isNOC}
        onRestart={handleRestart}
      />
    </Page>
  );
};
